/**
 * Follow-ups a routine suggested.
 *
 * A follow-up is held until the task it came from merges — work that builds on
 * a change nobody landed is usually moot — and is then offered on the
 * Suggested page, a menu item of its own, laid out like the task board: a list
 * and a detail pane. Accepting opens it as a real task, as suggested or edited
 * first; rejecting just takes it off the board.
 *
 * Deliberately not task rows: a suggestion is not work anybody has asked for
 * yet, and every queue, count and filter would otherwise have to learn to step
 * around it.
 */

import { memo, useMemo, useState } from 'react';
import type { Task, TaskFollowUp, TaskKind } from '@shared/domain.ts';
import { useNow, useRigg } from '@/data/RiggProvider.tsx';
import { api } from '@/lib/api.ts';
import { ago, cn } from '@/lib/format.ts';
import { navigate } from '@/lib/router.ts';
import { listRowClass } from './Board.tsx';
import { Markdown } from './Markdown.tsx';
import { KindPill, kindOptions } from './TaskFacts.tsx';
import {
  Button,
  Checkbox,
  Dialog,
  Field,
  Input,
  SectionLabel,
  Select,
  Textarea,
  Tip,
  useToast,
  type Option,
} from './ui.tsx';

/** Suggested follow-ups, newest first — what the board offers. */
export function suggestedFollowUps(followUps: Record<string, TaskFollowUp>): TaskFollowUp[] {
  return Object.values(followUps)
    .filter((f) => f.state === 'suggested')
    .sort((a, b) => (b.suggested_at ?? b.created_at).localeCompare(a.suggested_at ?? a.created_at));
}

/**
 * One suggestion on the Suggested page's list — laid out like a task row, on
 * the same pale card, so the page reads as a board and not as a form.
 */
export const SuggestedRow = memo(function SuggestedRow({
  followUp,
  origin,
  agentName,
  now,
  selected,
  onSelect,
}: {
  followUp: TaskFollowUp;
  origin: Task | null;
  agentName: string | null;
  now: number;
  selected: boolean;
  onSelect: (id: string | null) => void;
}) {
  const { reject, busy } = useFollowUpDecision(followUp);
  const toggle = () => onSelect(selected ? null : followUp.id);
  const gist = firstLine(followUp.prompt);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={selected}
      onClick={toggle}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggle();
        }
      }}
      className={cn(listRowClass(selected), 'group')}
    >
      <div className="flex min-w-0 items-center gap-x-2 sm:gap-x-2.5">
        <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-cardhi sm:text-[14.5px]">
          {followUp.title}
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={(event) => {
            event.stopPropagation();
            void reject();
          }}
          className={cn(
            'rg-focus shrink-0 cursor-pointer rounded-full border border-cardbtnbd bg-cardbtn px-3 py-1 text-[12px] font-semibold whitespace-nowrap text-cardbtntx',
            'transition-opacity duration-100 hover:bg-cardbtnhover hover:text-cardhi disabled:opacity-45',
            // Quiet at rest, like a task row's links — the row is for opening.
            selected ? 'opacity-100' : 'opacity-55 group-hover:opacity-100 focus-visible:opacity-100',
          )}
        >
          Reject
        </button>
      </div>

      {gist ? (
        <div className="truncate text-[12.5px] leading-relaxed text-cardbody sm:text-[13px]">{gist}</div>
      ) : null}

      <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] text-carddim sm:gap-x-2 sm:gap-y-1 sm:text-[12.5px]">
        <KindPill kind={followUp.kind} />
        {origin ? (
          <span className="min-w-0 truncate">
            from <span className="font-mono text-[11.5px] tabular-nums">#{origin.ref}</span>{' '}
            <span className="font-medium text-cardmid">{origin.title}</span>
          </span>
        ) : (
          <span>from a task</span>
        )}
        {agentName ? (
          <>
            <Dot />
            <span>{agentName}</span>
          </>
        ) : null}
        {followUp.auto_merge ? (
          <>
            <Dot />
            <span className="font-medium text-violet">Auto-merge</span>
          </>
        ) : null}
        {followUp.smart ? (
          <>
            <Dot />
            <span className="font-medium text-violet">Smart</span>
          </>
        ) : null}
        <Dot />
        <span className="shrink-0">{ago(followUp.suggested_at ?? followUp.created_at, now)}</span>
      </div>
    </div>
  );
});

const Dot = () => <span aria-hidden>·</span>;

/**
 * The first line of a prompt that says something — headings skipped, since a
 * routine's "## Problem" is the same on every row — minus list and quote marks.
 */
function firstLine(prompt: string): string {
  const line = prompt.split('\n').find((l) => l.trim() !== '' && !/^\s*#/.test(l)) ?? '';
  return line.replace(/^\s*([-*>]|\d+\.)\s+/, '').trim();
}

/**
 * Accepting and rejecting a follow-up, with the toasts that go with them.
 * Shared by the list's rows, the detail pane and the dialog.
 */
function useFollowUpDecision(followUp: TaskFollowUp, onDone?: () => void) {
  const { notify } = useToast();
  const [busy, setBusy] = useState(false);

  const accept = async (input: Parameters<typeof api.acceptFollowUp>[1]) => {
    setBusy(true);
    try {
      const result = await api.acceptFollowUp(followUp.id, input);
      notify(`Opened #${result.task.ref}`, {
        body: result.dispatched ? 'Sent to a routine.' : result.queued ? 'Queued until a routine has room.' : undefined,
      });
      onDone?.();
      navigate(`/t/${result.task.id}`, true);
    } catch (error) {
      notify('Could not accept it', { body: (error as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  /** True once it is off the list. */
  const reject = async (): Promise<boolean> => {
    setBusy(true);
    try {
      await api.rejectFollowUp(followUp.id);
      onDone?.();
      return true;
    } catch (error) {
      notify('Could not reject it', { body: (error as Error).message, tone: 'bad' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { accept, reject, busy };
}

/** The editable spec — title, prompt, routine, auto-merge and smart — and its Accept. */
function useFollowUpForm(followUp: TaskFollowUp, onDone?: () => void) {
  const { data } = useRigg();
  const decision = useFollowUpDecision(followUp, onDone);
  const [title, setTitle] = useState(followUp.title);
  const [prompt, setPrompt] = useState(followUp.prompt);
  const [autoMerge, setAutoMerge] = useState(followUp.auto_merge);
  const [smart, setSmart] = useState(followUp.smart);
  // The routine's suggestion, as a starting point: the person accepting decides.
  const [kind, setKind] = useState<string>(followUp.kind ?? 'none');
  const [agentId, setAgentId] = useState('auto');

  // Same rule as the composer: a design worker is never dispatched to, and a
  // routine from another project could not take this.
  const routines = useMemo(
    () =>
      Object.values(data.agents).filter((a) => a.project_id === followUp.project_id && a.kind !== 'design'),
    [data.agents, followUp.project_id],
  );
  const hasSmartAgent = routines.some((a) => a.intelligence === 'smart');
  const agentOptions: Option[] = [
    { value: 'auto', label: 'Auto — most room' },
    ...routines.map((agent) => ({ value: agent.id, label: agent.name, hint: agent.paused ? 'paused' : undefined })),
  ];

  const accept = () =>
    decision.accept({
      title: title.trim() || undefined,
      prompt: prompt.trim() || undefined,
      auto_merge: autoMerge,
      smart,
      kind: kind === 'none' ? null : (kind as TaskKind),
      agent_id: agentId === 'auto' ? null : agentId,
    });

  const canAccept = !decision.busy && title.trim() !== '' && prompt.trim() !== '';

  const fields = (rows: number) => (
    <>
      <Field label="Title">
        <Input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} />
      </Field>
      <Field label="What to do">
        <Textarea rows={rows} value={prompt} onChange={(event) => setPrompt(event.target.value)} />
      </Field>
      <div className="flex flex-wrap items-center gap-3">
        <Select size="sm" value={agentId} onChange={setAgentId} options={agentOptions} aria-label="Which routine" />
        <Select size="sm" value={kind} onChange={setKind} options={kindOptions('No kind')} aria-label="Kind" />
        <Tip content="Nobody has to click approve — Rigg lands it once it is clean, green and reviewed.">
          <span>
            <Checkbox id={`follow-up-auto-merge-${followUp.id}`} checked={autoMerge} onChange={setAutoMerge} label="Auto-merge" />
          </span>
        </Tip>
        {hasSmartAgent || smart ? (
          <Tip content="Sends this to whichever smart routine is next in rotation instead of an ordinary one.">
            <span>
              <Checkbox id={`follow-up-smart-${followUp.id}`} checked={smart} onChange={setSmart} label="Smart" />
            </span>
          </Tip>
        ) : null}
      </div>
    </>
  );

  return { ...decision, accept, canAccept, fields };
}

/**
 * A suggestion in the Suggested page's detail pane — the same place, and the
 * same header, a task's detail takes on the board. Everything is editable in
 * place; there is no dialog to open first.
 */
export function FollowUpDetail({
  followUp,
  onClose,
  onRejected,
}: {
  followUp: TaskFollowUp;
  onClose: () => void;
  onRejected: () => void;
}) {
  const { data, projects } = useRigg();
  const now = useNow();
  const origin = data.tasks[followUp.task_id] ?? null;
  const project = projects.find((p) => p.id === followUp.project_id);
  const proposer = followUp.agent_id ? (data.agents[followUp.agent_id]?.name ?? null) : null;
  const form = useFollowUpForm(followUp);

  const reject = async () => {
    if (await form.reject()) onRejected();
  };

  return (
    <div className="flex min-h-0 w-full flex-col">
      <div className="shrink-0 border-b border-line px-4 pt-4 pb-4 sm:px-6 sm:pt-5">
        <div className="flex items-center gap-2.5">
          <span className="shrink-0 rounded-full bg-violet/25 px-2.5 py-[3px] text-[11.5px] font-semibold text-violetx">
            Suggested
          </span>
          <span className="truncate text-[12px] font-medium text-mute">
            {project?.name ? `${project.name} · ` : ''}
            {proposer ? `by ${proposer} · ` : ''}
            {ago(followUp.suggested_at ?? followUp.created_at, now)}
          </span>
          <span className="flex-1" />
          <button
            onClick={onClose}
            title="Close details"
            className="rg-focus grid size-7 shrink-0 place-items-center rounded-full text-soft hover:bg-btn hover:text-hi2"
          >
            ✕
          </button>
        </div>

        <h2 className="mt-2.5 text-[17.5px] leading-snug font-[650] tracking-[-0.01em] text-hi">{followUp.title}</h2>

        {origin ? (
          <a
            href={`/t/${origin.id}`}
            onClick={(event) => {
              event.preventDefault();
              navigate(`/t/${origin.id}`);
            }}
            className="mt-3.5 flex min-w-0 items-center gap-2 rounded-xl border border-line bg-panel px-3.5 py-2.5 text-[13px] no-underline hover:border-inputbd"
            title="Open the task this came from"
          >
            <span className="shrink-0 text-[11px] font-semibold text-mute">From</span>
            <span className="shrink-0 font-mono text-[12px] text-soft tabular-nums">#{origin.ref}</span>
            <span className="min-w-0 flex-1 truncate text-t1">{origin.title}</span>
            <span className="shrink-0 text-mute" aria-hidden>
              →
            </span>
          </a>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2">
          <Button tone="primary" disabled={!form.canAccept} onClick={() => void form.accept()}>
            {form.busy ? 'Opening…' : 'Accept as a task'}
          </Button>
          <Button tone="danger" disabled={form.busy} onClick={() => void reject()}>
            Reject
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        <SectionLabel className="mb-3">Edit it as much as you like before it goes</SectionLabel>
        <div className="flex flex-col gap-4">{form.fields(16)}</div>
      </div>
    </div>
  );
}

/** Accept a follow-up — the spec, the routine, auto-merge and smart, all editable first. */
export function AcceptFollowUpDialog({ followUp, onClose }: { followUp: TaskFollowUp; onClose: () => void }) {
  const { data } = useRigg();
  const origin = data.tasks[followUp.task_id];
  const form = useFollowUpForm(followUp, onClose);

  return (
    <Dialog
      open
      wide
      onOpenChange={(next) => (next ? undefined : onClose())}
      title="Suggested follow-up"
      description={
        origin
          ? `Suggested while working on #${origin.ref} — ${origin.title}. Edit it as much as you like before it goes.`
          : 'Edit it as much as you like before it goes.'
      }
      footer={
        <>
          <Button tone="danger" disabled={form.busy} onClick={() => void form.reject()}>
            Reject
          </Button>
          <span className="flex-1" />
          <Button disabled={form.busy} onClick={onClose}>
            Later
          </Button>
          <Button tone="primary" disabled={!form.canAccept} onClick={() => void form.accept()}>
            {form.busy ? 'Opening…' : 'Accept as a task'}
          </Button>
        </>
      }
    >
      {form.fields(8)}
    </Dialog>
  );
}

const STATE_WORDS: Record<TaskFollowUp['state'], string> = {
  pending: 'offered once this merges',
  suggested: 'on the board',
  accepted: 'accepted',
  rejected: 'rejected',
};

/** The follow-ups a task's routine suggested, on that task's detail pane. */
export function TaskFollowUps({ task }: { task: Task }) {
  const { data } = useRigg();
  const [open, setOpen] = useState<TaskFollowUp | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const followUps = useMemo(
    () =>
      Object.values(data.followUps)
        .filter((f) => f.task_id === task.id)
        .sort((a, b) => a.created_at.localeCompare(b.created_at)),
    [data.followUps, task.id],
  );

  if (followUps.length === 0) return null;

  return (
    <div className="flex flex-col gap-1.5">
      {followUps.map((followUp) => {
        const created = followUp.created_task_id ? data.tasks[followUp.created_task_id] : null;
        const decidable = followUp.state === 'pending' || followUp.state === 'suggested';
        const isExpanded = expanded.has(followUp.id);
        const detailsId = `follow-up-details-${followUp.id}`;
        return (
          <div key={followUp.id} className="rounded-lg border border-line bg-panel text-[13px]">
            <div className="flex items-center gap-2.5 px-3 py-2">
              {/* The title is cut to one line; the row opens to the whole suggestion. */}
              <button
                type="button"
                aria-expanded={isExpanded}
                aria-controls={detailsId}
                onClick={() => toggle(followUp.id)}
                title={isExpanded ? 'Hide details' : 'Show details'}
                className="rg-focus flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded text-left"
              >
                <span
                  aria-hidden
                  className={cn('shrink-0 text-[10px] text-mute transition-transform duration-100', isExpanded && 'rotate-90')}
                >
                  ▶
                </span>
                <span
                  className={cn(
                    'min-w-0 flex-1',
                    isExpanded ? 'font-medium' : 'truncate',
                    followUp.state === 'rejected' ? 'text-mute line-through' : 'text-t1',
                  )}
                >
                  {followUp.title}
                </span>
              </button>
              {created ? (
                <a
                  href={`/t/${created.id}`}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate(`/t/${created.id}`, true);
                  }}
                  className="rg-focus shrink-0 text-[12px] text-accent hover:underline"
                >
                  #{created.ref}
                </a>
              ) : (
                <span className="shrink-0 text-[12px] text-mute">{STATE_WORDS[followUp.state]}</span>
              )}
              {decidable ? (
                <Button size="sm" onClick={() => setOpen(followUp)}>
                  {followUp.state === 'pending' ? 'Accept now' : 'Review'}
                </Button>
              ) : null}
            </div>
            {isExpanded ? (
              <div id={detailsId} className="border-t border-line px-3 py-2.5 text-[13px] leading-relaxed text-t1">
                {followUp.prompt.trim() ? (
                  <Markdown>{followUp.prompt}</Markdown>
                ) : (
                  <span className="text-mute">No details given.</span>
                )}
                {followUp.auto_merge || followUp.smart ? (
                  <div className="mt-2 flex gap-2 text-[12px] font-medium text-violet">
                    {followUp.auto_merge ? <span>Auto-merge</span> : null}
                    {followUp.smart ? <span>Smart</span> : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
      {open ? <AcceptFollowUpDialog followUp={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}
